// eslint-disable-next-line import/named
import { HotspotData } from "@google/model-viewer/lib/features/annotation";

interface OnClickProps {
  pressed: boolean;
  setPressed: React.Dispatch<React.SetStateAction<boolean>>;
  productDimensionsIsEnabled?: boolean;
  parentRef: React.RefObject<HTMLDivElement>;
}

const OnClick = ({
  pressed,
  setPressed,
  productDimensionsIsEnabled,
  parentRef,
}: OnClickProps) => {
  if (!productDimensionsIsEnabled) return;

  setPressed(!pressed);

  const modelViewer = parentRef.current?.querySelector("model-viewer");

  if (modelViewer == null) return;

  const setVisibility = (visible: boolean) => {
    const dimElements = [
      ...modelViewer.querySelectorAll("button"),
      modelViewer.querySelector("#dimLines"),
    ];

    dimElements.forEach((element) => {
      if (!element) return;

      if (visible) {
        element.classList.remove("hide");
      } else {
        element.classList.add("hide");
      }
    });
  };

  setVisibility(!pressed);

  if (pressed) return;

  const drawLine = (
    svgLine: SVGLineElement | undefined,
    dotHotspot1: HotspotData | null,
    dotHotspot2: HotspotData | null,
    dimensionHotspot?: HotspotData | null,
  ) => {
    if (svgLine && dotHotspot1 && dotHotspot2) {
      svgLine.setAttribute("x1", dotHotspot1.canvasPosition.x.toString());
      svgLine.setAttribute("y1", dotHotspot1.canvasPosition.y.toString());
      svgLine.setAttribute("x2", dotHotspot2.canvasPosition.x.toString());
      svgLine.setAttribute("y2", dotHotspot2.canvasPosition.y.toString());

      // use provided optional hotspot to tie visibility of this svg line to
      if (dimensionHotspot && !dimensionHotspot.facingCamera) {
        svgLine.classList.add("hide");
      } else {
        svgLine.classList.remove("hide");
      }
    }
  };

  const dimLines = modelViewer.querySelectorAll("line");

  const renderSVG = () => {
    drawLine(
      dimLines[0],
      modelViewer.queryHotspot("hotspot-dot+X-Y+Z"),
      modelViewer.queryHotspot("hotspot-dot+X-Y-Z"),
      modelViewer.queryHotspot("hotspot-dim+X-Y"),
    );
    drawLine(
      dimLines[1],
      modelViewer.queryHotspot("hotspot-dot+X-Y-Z"),
      modelViewer.queryHotspot("hotspot-dot+X+Y-Z"),
      modelViewer.queryHotspot("hotspot-dim+X-Z"),
    );
    drawLine(
      dimLines[2],
      modelViewer.queryHotspot("hotspot-dot+X+Y-Z"),
      modelViewer.queryHotspot("hotspot-dot-X+Y-Z"),
    ); // always visible
    drawLine(
      dimLines[3],
      modelViewer.queryHotspot("hotspot-dot-X+Y-Z"),
      modelViewer.queryHotspot("hotspot-dot-X-Y-Z"),
      modelViewer.queryHotspot("hotspot-dim-X-Z"),
    );
    drawLine(
      dimLines[4],
      modelViewer.queryHotspot("hotspot-dot-X-Y-Z"),
      modelViewer.queryHotspot("hotspot-dot-X-Y+Z"),
      modelViewer.queryHotspot("hotspot-dim-X-Y"),
    );

    requestAnimationFrame(renderSVG);
  };

  const center = modelViewer.getBoundingBoxCenter();
  const size = modelViewer.getDimensions();
  const x2 = size.x / 2;
  const y2 = size.y / 2;
  const z2 = size.z / 2;
  let hotspot = null;

  const getInches = (dim: number) => parseFloat((dim / 2.54).toFixed(2));

  modelViewer.updateHotspot({
    name: "hotspot-dot+X-Y+Z",
    position: `${center.x + x2} ${center.y - y2} ${center.z + z2}`,
  });

  modelViewer.updateHotspot({
    name: "hotspot-dim+X-Y",
    position: `${center.x + x2 * 1.2} ${center.y - y2 * 1.1} ${center.z}`,
  });

  hotspot = modelViewer.querySelector('button[slot="hotspot-dim+X-Y"]');

  if (hotspot) {
    hotspot.textContent = `${getInches(size.z)} in`;
  }

  modelViewer.updateHotspot({
    name: "hotspot-dot+X-Y-Z",
    position: `${center.x + x2} ${center.y - y2} ${center.z - z2}`,
  });

  modelViewer.updateHotspot({
    name: "hotspot-dim+X-Z",
    position: `${center.x + x2 * 1.2} ${center.y} ${center.z - z2 * 1.2}`,
  });

  hotspot = modelViewer.querySelector('button[slot="hotspot-dim+X-Z"]');

  if (hotspot) {
    hotspot.textContent = `${getInches(size.y)} in`;
  }

  modelViewer.updateHotspot({
    name: "hotspot-dot+X+Y-Z",
    position: `${center.x + x2} ${center.y + y2} ${center.z - z2}`,
  });

  modelViewer.updateHotspot({
    name: "hotspot-dim+Y-Z",
    position: `${center.x} ${center.y + y2 * 1.1} ${center.z - z2 * 1.1}`,
  });

  hotspot = modelViewer.querySelector('button[slot="hotspot-dim+Y-Z"]');

  if (hotspot) {
    hotspot.textContent = `${getInches(size.x)} in`;
  }

  modelViewer.updateHotspot({
    name: "hotspot-dot-X+Y-Z",
    position: `${center.x - x2} ${center.y + y2} ${center.z - z2}`,
  });

  modelViewer.updateHotspot({
    name: "hotspot-dim-X-Z",
    position: `${center.x - x2 * 1.2} ${center.y} ${center.z - z2 * 1.2}`,
  });

  hotspot = modelViewer.querySelector('button[slot="hotspot-dim-X-Z"]');

  if (hotspot) {
    hotspot.textContent = `${getInches(size.y)} in`;
  }

  modelViewer.updateHotspot({
    name: "hotspot-dot-X-Y-Z",
    position: `${center.x - x2} ${center.y - y2} ${center.z - z2}`,
  });

  modelViewer.updateHotspot({
    name: "hotspot-dim-X-Y",
    position: `${center.x - x2 * 1.2} ${center.y - y2 * 1.1} ${center.z}`,
  });

  hotspot = modelViewer.querySelector('button[slot="hotspot-dim-X-Y"]');

  if (hotspot) {
    hotspot.textContent = `${getInches(size.z)} in`;
  }

  modelViewer.updateHotspot({
    name: "hotspot-dot-X-Y+Z",
    position: `${center.x - x2} ${center.y - y2} ${center.z + z2}`,
  });

  renderSVG();
};

export default OnClick;
